import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Divider,
  Group,
  Loader,
  Progress,
  Radio,
  ScrollArea,
  Stack,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core'
import {
  IconCheck,
  IconPencil,
  IconPlayerStop,
  IconRefresh,
  IconSend,
  IconTrash,
  IconX,
} from '@tabler/icons-react'
import { useMedplum } from '@medplum/react'
import { useOrpc } from '@/hooks/useClient'
import type { InviteOrgMemberInput } from '@/hooks/useInviteOrgMember'
import { BATCH_ROW_CAP, parseBatchPaste } from './parse-batch-paste'
import {
  duplicateRowKeys,
  rowIssues,
  type InviteRow,
  type InviteRowStatus,
  type RowField,
  type RowIssue,
} from './invite-rows'
import { SEND_CONCURRENCY, classifyInviteError, runBatchPool } from './batch-send'

type BatchInviteRole = InviteOrgMemberInput['role']

type BatchPhase = 'edit' | 'sending' | 'done'

const ROW_FIELDS: { field: RowField; label: string }[] = [
  { field: 'firstName', label: 'First name' },
  { field: 'lastName', label: 'Last name' },
  { field: 'email', label: 'Email' },
]

const isRetryable = (status: InviteRowStatus) => status === 'failed' || status === 'notSent'

function RowIssuesText({ issues }: { issues: RowIssue[] }) {
  return (
    <Text size="xs" c="red">
      {issues.map((issue) => issue.message).join(' · ')}
    </Text>
  )
}

function RowStatusCell({
  row,
  issues,
  isDuplicate,
  phase,
}: {
  row: InviteRow
  issues: RowIssue[]
  isDuplicate: boolean
  phase: BatchPhase
}) {
  if (phase === 'edit') {
    if (issues.length > 0) return <RowIssuesText issues={issues} />
    if (isDuplicate) {
      return (
        <Text size="xs" c="yellow.8">
          Duplicate email in this batch
        </Text>
      )
    }
    return (
      <Text size="xs" c="dimmed">
        Ready
      </Text>
    )
  }

  // An edited row must re-validate before it is eligible for retry.
  if (issues.length > 0 && isRetryable(row.status)) return <RowIssuesText issues={issues} />

  switch (row.status) {
    case 'pending':
      return (
        <Text size="xs" c="dimmed">
          Pending
        </Text>
      )
    case 'sending':
      return (
        <Group gap={6}>
          <Loader size={14} />
          <Text size="xs">Sending…</Text>
        </Group>
      )
    case 'invited':
      return (
        <Group gap={6}>
          <IconCheck size={14} color="var(--mantine-color-green-7)" />
          <Text size="xs" c="green.7">
            Invited
          </Text>
        </Group>
      )
    case 'alreadyMember':
      return (
        <Text size="xs" c="yellow.8">
          Email already in use
        </Text>
      )
    case 'failed':
      return (
        <Tooltip label={row.errorMessage} multiline maw={420}>
          <Group gap={6} wrap="nowrap">
            <IconX size={14} color="var(--mantine-color-red-7)" />
            <Text size="xs" c="red.7" truncate style={{ maxWidth: 220 }}>
              {row.errorMessage}
            </Text>
          </Group>
        </Tooltip>
      )
    case 'notSent':
      return (
        <Text size="xs" c="dimmed">
          Not sent
        </Text>
      )
  }
}

/**
 * Batch Invite mode of the invite-member modal: paste rows copied from Excel,
 * review them, send one invitation per row over the single-invite endpoint.
 */
export function BatchInvitePanel({
  organizationId,
  onInvited,
  onPreviewStepChange,
  onSendingChange,
}: {
  organizationId?: string
  onInvited?: () => void
  /** The modal grows to fit the grid while the Preview step is active. */
  onPreviewStepChange: (onPreview: boolean) => void
  /** The modal locks its close affordances while a send loop is running. */
  onSendingChange: (sending: boolean) => void
}) {
  const medplum = useMedplum()
  const orpc = useOrpc()

  const [step, setStepState] = useState(0)
  const [role, setRole] = useState<BatchInviteRole>('practitioner')
  const [pasteText, setPasteText] = useState('')
  const [rows, setRows] = useState<InviteRow[]>([])
  const [parseMeta, setParseMeta] = useState({ headerSkipped: false, droppedOverCap: 0 })
  const [editingKeys, setEditingKeys] = useState<Set<number>>(new Set())
  const [phase, setPhase] = useState<BatchPhase>('edit')
  const [runTotal, setRunTotal] = useState(0)
  const [runDone, setRunDone] = useState(0)
  const nextKeyRef = useRef(1)
  const stopRef = useRef(false)

  const parsed = useMemo(() => parseBatchPaste(pasteText), [pasteText])

  const duplicates = duplicateRowKeys(rows)
  const errorCount = rows.filter((row) => rowIssues(row).length > 0).length
  const canSend = phase === 'edit' && rows.length > 0 && errorCount === 0 && duplicates.size === 0
  const countOf = (status: InviteRowStatus) => rows.filter((row) => row.status === status).length

  // Warn on tab close while a send loop is running.
  useEffect(() => {
    if (phase !== 'sending') return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [phase])

  const setStep = (next: number) => {
    setStepState(next)
    onPreviewStepChange(next === 2)
  }

  const setRowStatus = (key: number, status: InviteRowStatus, errorMessage?: string) =>
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, status, errorMessage } : row)))

  const runSendLoop = async (targets: InviteRow[]) => {
    if (!organizationId || targets.length === 0) return
    setPhase('sending')
    onSendingChange(true)
    stopRef.current = false
    setRunTotal(targets.length)
    setRunDone(0)

    await runBatchPool(
      targets,
      SEND_CONCURRENCY,
      async (target) => {
        setRowStatus(target.key, 'sending')
        try {
          await orpc.inviteOrgMember.call({
            organizationId,
            role,
            firstName: target.firstName,
            lastName: target.lastName,
            email: target.email,
            licenses: [],
          })
          setRowStatus(target.key, 'invited')
        } catch (error) {
          const { status, message } = classifyInviteError(error)
          setRowStatus(target.key, status, message)
        }
        setRunDone((count) => count + 1)
      },
      () => stopRef.current,
    )

    // Rows the pool never dispatched (Stop) become Not sent.
    setRows((prev) =>
      prev.map((row) => (row.status === 'pending' ? { ...row, status: 'notSent' } : row)),
    )
    setPhase('done')
    onSendingChange(false)

    // Refresh side effects fire once per run, at loop end (not per row).
    onInvited?.()
    medplum.invalidateSearches('Practitioner')
    medplum.invalidateSearches('ProjectMembership')
  }

  // Failed and Not-sent rows retry; an edited row must re-validate first.
  const eligibleRetryRows = rows.filter(
    (row) => isRetryable(row.status) && rowIssues(row).length === 0,
  )

  const handleRetry = () => {
    const targetKeys = new Set(eligibleRetryRows.map((row) => row.key))
    setRows((prev) =>
      prev.map((row) =>
        targetKeys.has(row.key) ? { ...row, status: 'pending', errorMessage: undefined } : row,
      ),
    )
    void runSendLoop(eligibleRetryRows)
  }

  const handlePreview = () => {
    const materialized: InviteRow[] = parsed.rows.map((fields) => ({
      ...fields,
      key: nextKeyRef.current++,
      status: 'pending',
    }))
    setRows(materialized)
    setParseMeta({ headerSkipped: parsed.headerSkipped, droppedOverCap: parsed.droppedOverCap })
    setEditingKeys(
      new Set(materialized.filter((row) => rowIssues(row).length > 0).map((row) => row.key)),
    )
    setStep(2)
  }

  const setRowField = (key: number, field: RowField, value: string) =>
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, [field]: value } : row)))

  const removeRow = (key: number) => setRows((prev) => prev.filter((row) => row.key !== key))

  return (
    <Tabs value={step.toString()} onChange={(value) => setStep(Number(value))}>
      <Tabs.List>
        <Tabs.Tab value="0" disabled={phase !== 'edit'}>
          Role Selection
        </Tabs.Tab>
        <Tabs.Tab value="1" disabled={phase !== 'edit'}>
          Paste Rows
        </Tabs.Tab>
        <Tabs.Tab value="2" disabled={rows.length === 0}>
          Preview &amp; Send
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="0">
        <Stack py="md">
          <Title order={4}>Select Member Role</Title>
          <Radio.Group
            value={role}
            onChange={(value) => setRole(value as BatchInviteRole)}
            label="Role Type"
            description="Every member in this batch will be invited with this role."
            withAsterisk
          >
            <Stack mt="xs">
              <Radio value="practitioner" label="Practitioner" />
            </Stack>
            <Stack mt="xs">
              <Radio value="scheduleManager" label="Care Coordinator" />
            </Stack>
          </Radio.Group>

          <Alert color="blue" title="Role Information">
            {role === 'practitioner' && (
              <Text size="sm">
                A <strong>Practitioner</strong> role is appropriate for doctors, nurses, and other
                healthcare providers that will be providing care within this organization.
              </Text>
            )}
            {role === 'scheduleManager' && (
              <Text size="sm">
                A <strong>Care Coordinator</strong> role is responsible for maintaining
                practitioners' schedules, managing appointment slots, and handling appointment
                creation and modifications within the organization.
              </Text>
            )}
          </Alert>

          <Divider />
          <Group justify="flex-end">
            <Button onClick={() => setStep(1)} type="button">
              Next
            </Button>
          </Group>
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="1">
        <Stack py="md">
          <Title order={4}>Paste Rows</Title>
          <Text size="sm" c="dimmed">
            Copy three columns from Excel — First name, Last name, Email — and paste them below. A
            header row is detected and skipped. Up to {BATCH_ROW_CAP} rows per batch.
          </Text>
          <Textarea
            aria-label="Pasted rows"
            autosize
            minRows={8}
            maxRows={14}
            placeholder={'Ava\tThompson\tava.thompson@example.com'}
            value={pasteText}
            onChange={(event) => setPasteText(event.currentTarget.value)}
            styles={{ input: { fontFamily: 'monospace' } }}
          />
          <Group gap="xs">
            {parsed.rows.length > 0 && (
              <Badge variant="light">
                {parsed.rows.length} row{parsed.rows.length === 1 ? '' : 's'}
              </Badge>
            )}
            {parsed.headerSkipped && (
              <Badge variant="light" color="gray">
                header skipped
              </Badge>
            )}
            {parsed.droppedOverCap > 0 && (
              <Badge variant="light" color="orange">
                {parsed.droppedOverCap} dropped over cap
              </Badge>
            )}
          </Group>
          <Divider />
          <Group justify="flex-end">
            <Button disabled={parsed.rows.length === 0} onClick={handlePreview} type="button">
              Preview rows
            </Button>
          </Group>
        </Stack>
      </Tabs.Panel>

      <Tabs.Panel value="2">
        <Stack py="md">
          <Group justify="space-between">
            <Title order={4}>Preview &amp; Send</Title>
            <Group gap="xs">
              <Badge variant="light">
                {rows.length} row{rows.length === 1 ? '' : 's'}
              </Badge>
              {parseMeta.headerSkipped && (
                <Badge variant="light" color="gray">
                  header skipped
                </Badge>
              )}
              {parseMeta.droppedOverCap > 0 && (
                <Badge variant="light" color="orange">
                  {parseMeta.droppedOverCap} dropped over cap
                </Badge>
              )}
              {errorCount > 0 && (
                <Badge variant="light" color="red">
                  {errorCount} error{errorCount === 1 ? '' : 's'}
                </Badge>
              )}
              {duplicates.size > 0 && (
                <Badge variant="light" color="yellow">
                  {duplicates.size} duplicate{duplicates.size === 1 ? '' : 's'}
                </Badge>
              )}
            </Group>
          </Group>

          <ScrollArea h={380} type="auto">
            <Table stickyHeader striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={40}>#</Table.Th>
                  <Table.Th>First name</Table.Th>
                  <Table.Th>Last name</Table.Th>
                  <Table.Th>Email</Table.Th>
                  <Table.Th w={260}>Status</Table.Th>
                  <Table.Th w={80} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((row, index) => {
                  const issues = rowIssues(row)
                  const isDuplicate = duplicates.has(row.key)
                  const rowEditable =
                    phase === 'edit' || (phase === 'done' && isRetryable(row.status))
                  const editing = rowEditable && editingKeys.has(row.key)
                  const tintRed =
                    phase === 'edit'
                      ? issues.length > 0
                      : row.status === 'failed' || (isRetryable(row.status) && issues.length > 0)
                  const tintYellow = phase === 'edit' ? isDuplicate : row.status === 'alreadyMember'
                  return (
                    <Table.Tr
                      key={row.key}
                      style={
                        tintRed
                          ? { background: 'var(--mantine-color-red-0)' }
                          : tintYellow
                            ? { background: 'var(--mantine-color-yellow-0)' }
                            : undefined
                      }
                    >
                      <Table.Td>{index + 1}</Table.Td>
                      {ROW_FIELDS.map(({ field, label }) => (
                        <Table.Td key={field}>
                          {editing ? (
                            <TextInput
                              size="xs"
                              aria-label={label}
                              value={row[field]}
                              error={issues.find((issue) => issue.field === field)?.message}
                              onChange={(event) =>
                                setRowField(row.key, field, event.currentTarget.value)
                              }
                            />
                          ) : (
                            <Text size="sm">{row[field]}</Text>
                          )}
                        </Table.Td>
                      ))}
                      <Table.Td>
                        <RowStatusCell
                          row={row}
                          issues={issues}
                          isDuplicate={isDuplicate}
                          phase={phase}
                        />
                      </Table.Td>
                      <Table.Td>
                        {rowEditable && (
                          <Group gap={4} wrap="nowrap">
                            {editing ? (
                              <Tooltip label="Confirm row">
                                <ActionIcon
                                  variant="subtle"
                                  color="green"
                                  aria-label="Confirm row"
                                  onClick={() =>
                                    setEditingKeys((prev) => {
                                      const next = new Set(prev)
                                      next.delete(row.key)
                                      return next
                                    })
                                  }
                                >
                                  <IconCheck size={16} />
                                </ActionIcon>
                              </Tooltip>
                            ) : (
                              <Tooltip label="Edit row">
                                <ActionIcon
                                  variant="subtle"
                                  color="gray"
                                  aria-label="Edit row"
                                  onClick={() =>
                                    setEditingKeys((prev) => new Set(prev).add(row.key))
                                  }
                                >
                                  <IconPencil size={16} />
                                </ActionIcon>
                              </Tooltip>
                            )}
                            <Tooltip label="Remove row">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                aria-label="Remove row"
                                onClick={() => removeRow(row.key)}
                              >
                                <IconTrash size={16} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
          </ScrollArea>

          {phase === 'sending' && (
            <>
              <Progress value={runTotal === 0 ? 0 : (runDone / runTotal) * 100} animated />
              <Text size="sm" c="dimmed">
                Inviting {Math.min(runDone + 1, runTotal)} of {runTotal}…
              </Text>
            </>
          )}
          {phase === 'done' && (
            <Alert
              color={countOf('failed') > 0 || countOf('notSent') > 0 ? 'yellow' : 'green'}
              title={`${countOf('invited')} invited, ${countOf('alreadyMember')} email${countOf('alreadyMember') === 1 ? '' : 's'} already in use, ${countOf('failed')} failed, ${countOf('notSent')} not sent`}
            >
              <Text size="sm">
                Each invited member receives an email with instructions to complete registration.
              </Text>
            </Alert>
          )}

          <Divider />
          <Group justify="flex-end">
            {phase === 'sending' && (
              <Button
                variant="light"
                color="red"
                leftSection={<IconPlayerStop size={16} />}
                onClick={() => {
                  stopRef.current = true
                }}
                type="button"
              >
                Stop
              </Button>
            )}
            {phase === 'edit' && (
              <Button
                leftSection={<IconSend size={16} />}
                disabled={!canSend}
                onClick={() => void runSendLoop(rows)}
                type="button"
              >
                Send {rows.length} invitation{rows.length === 1 ? '' : 's'}
              </Button>
            )}
            {phase === 'done' && (
              <Button
                leftSection={<IconRefresh size={16} />}
                disabled={eligibleRetryRows.length === 0}
                onClick={handleRetry}
                type="button"
              >
                Retry failed &amp; unsent rows
              </Button>
            )}
          </Group>
        </Stack>
      </Tabs.Panel>
    </Tabs>
  )
}
